import { jsx as _jsx } from "react/jsx-runtime";
import { useLocationPathname, useNavigate } from '@rocketlane/rocket-router';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { AccountFeature, isAccountFeatureEnabled, useAccountFeature } from 'hooks/use-account-feature';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { APP_SHELL_MODE_STORAGE_KEY, DEFAULT_APP_SHELL_MODE, getAppShellMode, isNitroClawPathname, persistAppShellMode } from 'utils/app-shell-mode';
import { isTauriApp } from 'utils/is-tauri';
const NITRO_CLAW_HOME = `/${ROUTE_PATHS.NITRO_CLAW}`;
const AppShellContext = /*#__PURE__*/ createContext(null);
const noopSetMode = (_mode)=>{};
const noopSwitchToAppShell = (_next)=>{};
/** Safe defaults when a test mounts a consumer without AppShellProvider. */ const TEST_FALLBACK_APP_SHELL = {
    mode: DEFAULT_APP_SHELL_MODE,
    setMode: noopSetMode,
    switchToAppShell: noopSwitchToAppShell,
    isNitroClawShellActive: false,
    isNitroClawEnabled: false
};
export const AppShellProvider = ({ children })=>{
    const navigate = useNavigate();
    const pathname = useLocationPathname();
    const [isNitroClawEnabled] = useAccountFeature(AccountFeature.NitroClaw);
    const [mode, setModeState] = useState(()=>getAppShellMode());
    const setMode = useCallback((next)=>{
        setModeState(next);
        persistAppShellMode(next);
    }, []);
    useEffect(()=>{
        const onStorage = (event)=>{
            if (event.key !== APP_SHELL_MODE_STORAGE_KEY || event.newValue == null) {
                return;
            }
            try {
                const parsed = JSON.parse(event.newValue);
                if (parsed !== 'rocketlane' && parsed !== 'nitro-claw') {
                    return;
                }
                setModeState(parsed);
                if (parsed === 'rocketlane' && isNitroClawPathname(window.location.pathname)) {
                    navigate({
                        pathname: ROUTE_PATHS.ROOT,
                        search: '',
                        hash: ''
                    });
                }
            } catch (unused) {}
        };
        window.addEventListener('storage', onStorage);
        return ()=>window.removeEventListener('storage', onStorage);
    }, [
        navigate
    ]);
    useEffect(()=>{
        if (!isTauriApp() && mode === 'nitro-claw' && !isAccountFeatureEnabled(AccountFeature.NitroClaw)) {
            setMode(DEFAULT_APP_SHELL_MODE);
        }
    }, [
        mode,
        setMode
    ]);
    useLayoutEffect(()=>{
        if (isTauriApp() || !isNitroClawEnabled) {
            return;
        }
        if (!isNitroClawPathname(pathname)) {
            return;
        }
        setModeState((prev)=>{
            if (prev === 'nitro-claw') {
                return prev;
            }
            persistAppShellMode('nitro-claw');
            return 'nitro-claw';
        });
    }, [
        pathname,
        isNitroClawEnabled
    ]);
    const switchToAppShell = useCallback((next)=>{
        if (next === 'nitro-claw') {
            navigate({
                pathname: NITRO_CLAW_HOME,
                search: '',
                hash: ''
            });
            if (isNitroClawPathname(pathname)) {
                setMode('nitro-claw');
            }
            return;
        }
        setMode('rocketlane');
        navigate({
            pathname: ROUTE_PATHS.ROOT,
            search: '',
            hash: ''
        });
    }, [
        navigate,
        pathname,
        setMode
    ]);
    const isNitroClawShellActive = isNitroClawEnabled && (isTauriApp() || mode === 'nitro-claw');
    const value = useMemo(()=>({
            mode,
            setMode,
            switchToAppShell,
            isNitroClawShellActive,
            isNitroClawEnabled
        }), [
        mode,
        setMode,
        switchToAppShell,
        isNitroClawShellActive,
        isNitroClawEnabled
    ]);
    return /*#__PURE__*/ _jsx(AppShellContext.Provider, {
        value: value,
        children: children
    });
};
export const useAppShellContext = ()=>{
    const value = useContext(AppShellContext);
    if (value == null) {
        if (process.env.NODE_ENV === 'test') {
            return TEST_FALLBACK_APP_SHELL;
        }
        throw new Error('useAppShellMode must be used within AppShellProvider');
    }
    return value;
};
